import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Handshake, LayoutDashboard, UserCheck, MessageSquare,
  FolderGit2, GraduationCap, Users, Award, TrendingUp,
  Rocket, Zap, ChevronDown
} from 'lucide-react';
import ScrollReveal from '../effects/ScrollReveal';

const features = [
  { icon: Handshake, title: 'Placement Assistance', desc: 'We stay with you until you get placed - no time limits, no excuses. Period.' },
  { icon: LayoutDashboard, title: 'Student Dashboard', desc: 'Course progress tracking, module completion, and interview readiness score in one place.' },
  { icon: UserCheck, title: 'Dedicated Career Manager', desc: 'One-on-one mentorship, weekly progress calls, and personalized guidance throughout.' },
  { icon: MessageSquare, title: 'Soft Skills & HR Training', desc: 'Resume building, LinkedIn optimization, HR interview training, and salary negotiation.' },
  { icon: FolderGit2, title: 'Portfolio Building', desc: 'Every student completes 3-5 real projects to showcase their skills to employers.' },
  { icon: Rocket, title: 'Internship Program', desc: 'Real-time projects, GitHub portfolio, and an official internship certificate.' },
  { icon: Users, title: 'Referral Network', desc: 'Active alumni community, internal referrals to partner companies, and job alerts.' },
  { icon: Award, title: 'XceedEdge Certification', desc: 'Industry-recognized XceedEdge Certified Professional credential upon completion.' },
  { icon: TrendingUp, title: 'Market-Oriented Training', desc: 'Hiring process understanding, resume ATS training, and interview strategy sessions.' },
  { icon: GraduationCap, title: 'Career Gap Support', desc: 'We specialize in restarting careers - no matter how long the gap has been.' },
  { icon: Zap, title: 'Extra Learning Boosters', desc: 'Hackathons, weekly challenges, leaderboard, interview question bank, and recorded sessions.' },
];

export default function CareerSupport() {
  const [expanded, setExpanded] = useState(null);

  return (
    <section className="py-24 relative overflow-hidden">
      <motion.div
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-[15%] left-[3%] w-[450px] h-[450px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(254,210,2,0.1) 0%, transparent 65%)' }}
      />
      <motion.div
        animate={{ scale: [1.1, 1, 1.1] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
        className="absolute bottom-[10%] right-[3%] w-[350px] h-[350px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(254,210,2,0.07) 0%, transparent 65%)' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        <ScrollReveal variant="fadeUp" className="text-center mb-16">
          <span className="inline-block text-primary text-sm font-bold tracking-widest uppercase mb-3 px-4 py-1.5 rounded-full glass-yellow">Full Support</span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-4">
            Career Transformation <span className="text-primary relative">
              Support
              <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.6 }}
                className="absolute -bottom-1.5 left-0 right-0 h-0.5 bg-primary rounded-full origin-left" />
            </span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-xl mx-auto text-lg font-body">
            Everything you need - from learning to landing your dream job.
          </p>
        </ScrollReveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feat, i) => {
            const FeatIcon = feat.icon;
            const isExpanded = expanded === i;
            return (
              <ScrollReveal key={feat.title} variant="fadeUp" delay={i * 0.04}>
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ type: 'spring', stiffness: 280, damping: 18 }}
                  onClick={() => setExpanded(isExpanded ? null : i)}
                  className="glass rounded-2xl border border-white/80 p-5 shadow-sm hover:shadow-xl hover:shadow-black/6 transition-all group cursor-pointer h-full relative overflow-hidden"
                >
                  {/* Hover glow */}
                  <motion.div
                    animate={{ opacity: isExpanded ? 1 : 0 }}
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: 'radial-gradient(ellipse at 0% 0%, rgba(254,210,2,0.08) 0%, transparent 60%)' }}
                  />

                  <div className="flex items-start gap-4 relative">
                    <motion.div
                      animate={isExpanded ? { scale: 1.1, rotate: -8, backgroundColor: 'hsl(47, 99%, 50%)' } : { scale: 1, rotate: 0 }}
                      className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors duration-300"
                    >
                      <FeatIcon className={`w-5 h-5 transition-colors duration-300 ${isExpanded ? 'text-primary-foreground' : 'text-primary'}`} />
                    </motion.div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-heading font-bold text-sm">{feat.title}</h3>
                        <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.3 }}>
                          <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        </motion.div>
                      </div>
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.p
                            initial={{ opacity: 0, height: 0, marginTop: 0 }}
                            animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
                            exit={{ opacity: 0, height: 0, marginTop: 0 }}
                            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                            className="text-xs text-muted-foreground leading-relaxed font-body overflow-hidden"
                          >
                            {feat.desc}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Bottom CTA strip */}
        <ScrollReveal variant="fadeUp" delay={0.2} className="mt-12">
          <div className="glass rounded-2xl border border-white/80 p-6 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
            <div>
              <h3 className="font-heading font-bold text-lg">Ready to transform your career?</h3>
              <p className="text-muted-foreground text-sm mt-1 font-body">Join 50,000+ students who already made the leap.</p>
            </div>
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-2xl bg-primary text-primary-foreground font-bold shadow-xl shadow-primary/30 hover:bg-primary/90 transition-colors whitespace-nowrap flex-shrink-0"
            >
              Start For Free <Zap className="w-4 h-4" />
            </motion.a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}